import { useCallback, useMemo } from 'react';
import {
  Order,
  ProductType,
  useAddProductToOrder,
  useRemoveProductFromOrder,
} from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { PriceEditionLine } from './PriceEditionLine';
import { useToast } from '@app/core';
import { computeIncentive } from 'lib/priceComputation';

interface Props {
  order: Order;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

export const OrderPriceEdition = ({ order }: Props) => {
  const classes = useStyles();
  const toast = useToast();

  const { mutateAsync: addProduct } = useAddProductToOrder(
    order.id,
    order.sessionId,
  );
  const { mutateAsync: removeProduct } = useRemoveProductFromOrder(
    order.id,
    order.sessionId,
  );

  const incentives = order.productLines?.filter(
    (planProduct) => planProduct.product?.type === ProductType.INCENTIVE,
  );
  const fees = order.productLines?.filter(
    (planProduct) => planProduct.product?.type === ProductType.PARTICIPANT_FEE,
  );

  const others = order.productLines?.filter(
    (planProduct) =>
      planProduct.product?.type !== ProductType.PARTICIPANT_FEE &&
      planProduct.product?.type !== ProductType.INCENTIVE,
  );

  const remove = useCallback(
    async (productId: string) => {
      try {
        await removeProduct({
          id: productId,
        });
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [removeProduct],
  );

  const add = useCallback(
    async (productId: string) => {
      try {
        await addProduct({
          id: productId,
        });
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [addProduct],
  );

  const incentive = useMemo(
    () => computeIncentive(order?.productLines || [], order?.duration),
    [order],
  );

  if (!order.participationId && !incentives?.length && !fees?.length) {
    return (
      <PriceEditionLine
        productLines={others}
        productType={ProductType.FEATURE}
        incentive={incentive}
        duration={order.duration}
        pricePerParticipant={false}
        addProduct={add}
        removeProduct={remove}
      />
    );
  }

  return (
    <div className={classes.container}>
      <PriceEditionLine
        label="Contrepartie"
        productLines={incentives}
        productType={ProductType.INCENTIVE}
        duration={order.duration}
        pricePerParticipant
        addProduct={incentives?.length === 0 ? add : undefined}
        removeProduct={remove}
      />
      <PriceEditionLine
        label="Frais"
        productLines={fees}
        productType={ProductType.PARTICIPANT_FEE}
        incentive={incentive}
        duration={order.duration}
        pricePerParticipant
        addProduct={fees?.length === 0 ? add : undefined}
        removeProduct={remove}
      />
      <PriceEditionLine
        label="Options"
        productLines={others}
        productType={ProductType.FEATURE}
        incentive={incentive}
        duration={order.duration}
        pricePerParticipant
        addProduct={add}
        removeProduct={remove}
      />
    </div>
  );
};
